// Apple In-App Purchase via cordova-plugin-purchase (CdvPurchase v13+).
//
// Build 32 rewrite — mirrors the Drift pattern (14-autosleep-tracker) verbatim
// after the build-31 iPad 2.1(b) rejection. The non-obvious mechanics that
// prevent "error after Subscribe tap" rejections:
//
//   1. `waitForCdv()` — never assume `window.CdvPurchase` is loaded synchronously
//      by Capacitor. The plugin bridge populates the global a tick after
//      onDeviceReady; calling getStore() before that returns null and a "Store
//      not available" toast lands in front of the reviewer.
//   2. `await initializeIAP()` at the TOP of `purchase()` — guarantees that
//      register/initialize/update have completed before the order goes out, so
//      product fetch races against navigation are gone.
//   3. `store.update()` pulses inside the product wait — iPadOS sandbox cold
//      installs sometimes need the catalog to be poked repeatedly before it
//      delivers metadata.
//   4. `setOnPurchaseUpdate` callback — the UI flips on the `approved` event,
//      NOT on `store.order()` resolving. This decouples purchase-success UX
//      from the order Promise. (Drift comment: "decouples UI from store.order()
//      resolving".)
//   5. Cancel codes: 6777003 (legacy observation, still seen on iPadOS) AND
//      6777006 (real SKErrorPaymentCancelled mapping) AND 6777010 (Ask-to-Buy
//      deferral). Plus a fuzzy `/cancel/i` match on the message — both
//      surface as a silent rethrow, never a red toast.
//
// v1.5.0 (docs/v150-spec.md §3.5) — the expiry / restore experience:
//   6. A launch re-delivery (`approved` OUTSIDE the user-initiated window) is
//      finished, then — signed in only — checked with POST
//      /subscription/apple-check. The owned-receipt marker is set only for
//      state 'live' + linked 'none', with the server's expiresAt. The Apple
//      adapter never exposes an expiry, so the client cannot judge "live".
//   7. `verified` classifies the apple-verify answers through
//      silentFetchDetailed (a 409 is not a failure), records the outcome in
//      iapStore and resolves the restore waiting for it. A batch that ends
//      'failed' has already been finished, so StoreKit will never hand it
//      back: each unconfirmed transaction goes to the same apple-check (6.)
//      straight away, so a payer whose link failed still gets the Restore
//      prompt. An apple-check with no definitive answer (here or at launch) is
//      kept and asked again on the next launch or sign-in
//      (retryPendingChecks).
//   8. The optimistic Premium patch is only for the product the user has just
//      ordered — a restore re-delivery never flips the UI to Premium.
//   9. restoreAndWait() reports what the restore actually found and never hangs.
//  10. store.applicationUsername = the Cognito sub (obfuscator off), so new
//      transactions carry appAccountToken.

import { silentFetch, silentFetchDetailed } from '../api/client';
import { isIOS } from '../utils/platform';
import { isOffline } from '../../services/network';
import {
  useIapStore,
  currentUserSub,
  readPendingChecks,
  notePendingCheck,
  forgetPendingCheck,
} from '../stores/iapStore';
import { getBilledPricing, getIntroTrial } from './iapPricing';

// Displayed/sellable SKUs — the only products surfaced in the app. The
// `.plus.*` naming is legacy: these SKUs are sold as "Premium" after the
// 2026-08 tier restructure collapsed Plus + Premium into a single paid tier.
// Prices and the introductory offer live in App Store Connect and are read
// from StoreKit at runtime (see iapPricing / usePlanPrices) — nothing here
// knows what they cost. The retired premium.*.v2 and all non-.v2 IDs stay
// registered with StoreKit via APPLE_TO_INTERNAL below — an unregistered
// product never fires restore events — but are never displayed.
const PRODUCT_IDS = [
  'uk.co.wolftechnologies.babytrack.plus.monthly.v2',
  'uk.co.wolftechnologies.babytrack.plus.yearly.v2',
];

// Keyed by the in-app plan name ("premium") — the SKU keeps its legacy id.
const PRODUCT_ID_MAP = {
  premium_month: 'uk.co.wolftechnologies.babytrack.plus.monthly.v2',
  premium_year: 'uk.co.wolftechnologies.babytrack.plus.yearly.v2',
};

const APPLE_TO_INTERNAL = {
  'uk.co.wolftechnologies.babytrack.plus.monthly.v2':    { plan: 'plus',    interval: 'month' },
  'uk.co.wolftechnologies.babytrack.plus.yearly.v2':     { plan: 'plus',    interval: 'year'  },
  'uk.co.wolftechnologies.babytrack.premium.monthly.v2': { plan: 'premium', interval: 'month' },
  'uk.co.wolftechnologies.babytrack.premium.yearly.v2':  { plan: 'premium', interval: 'year'  },
  'uk.co.wolftechnologies.babytrack.plus.monthly':       { plan: 'plus',    interval: 'month' },
  'uk.co.wolftechnologies.babytrack.plus.yearly':        { plan: 'plus',    interval: 'year'  },
  'uk.co.wolftechnologies.babytrack.premium.monthly':    { plan: 'premium', interval: 'month' },
  'uk.co.wolftechnologies.babytrack.premium.yearly':     { plan: 'premium', interval: 'year'  },
};

// Every known SKU, sellable or legacy — all must be registered so StoreKit
// surfaces legacy transactions to the approved/verified pipeline on restore.
const REGISTERED_PRODUCT_IDS = Object.keys(APPLE_TO_INTERNAL);

const USER_INITIATED_WINDOW_MS = 5 * 60 * 1000;
const CDV_WAIT_TIMEOUT_MS = 15_000;
const CDV_POLL_INTERVAL_MS = 100;
const PRODUCT_POLL_INTERVAL_MS = 200;
const PRODUCT_POLL_TIMEOUT_MS = 12_000;
const PRODUCT_PULSE_INTERVAL_MS = 3_000;

// restoreAndWait budgets. StoreKit hands restored transactions to `approved`
// ~1 s after store.restorePurchases() resolves (the adapter's 300 ms debounce
// plus the listener's 500 ms), so 8 s of silence means there was nothing to
// restore. The 25 s ceiling covers a cold apple-verify Lambda with an App
// Store Server API retry — past it the restore reports 'failed', never hangs.
// Both start when StoreKit hands control back: until then the user may be in
// Apple's own sign-in sheet, and their typing is not our wait. The backstop
// covers a StoreKit that never answers at all.
const RESTORE_NOTHING_AFTER_MS = 8_000;
const RESTORE_CEILING_MS = 25_000;
const RESTORE_BACKSTOP_MS = 120_000;
// The plugin's StoreAdapterListener does not re-fire `approved` for the same
// transaction within 60 s, so a second Restore inside that window is handed
// nothing. It reports the outcome the first one recorded instead of claiming
// there is nothing to restore.
const RECENT_OUTCOME_MS = 90_000;

// apple-check answers that settle the question. 'unknown' is not one: the
// server could not look the transaction up (often the same App Store Server
// API outage that failed apple-verify), so it is asked again later.
const DEFINITIVE_CHECK_STATES = new Set(['live', 'expired', 'revoked']);
// A failed restore can carry every renewal the Apple ID ever made. Only the
// newest few can still be live, so only they are re-checked.
const MAX_RECHECKS_PER_BATCH = 5;

// Cancel codes. 6777006 = SKErrorPaymentCancelled (Apple's real cancel).
// 6777003 = PURCHASE (generic, kept for back-compat with the build-22 observation).
// 6777010 = UNKNOWN / Ask-to-Buy deferral.
const CANCEL_CODES = new Set([6777006, 6777003, 6777010]);

// store.restorePurchases() RESOLVES with an IError instead of throwing. Two
// classes of result must NOT be surfaced as a failure:
//   - user cancels (dismissed the App Store sign-in sheet) — a red toast on a
//     purchase control is the Guideline 2.1(b) pattern that got build 31
//     rejected;
//   - REFRESH_RECEIPTS (6777011) — the adapter refreshes the receipt AFTER the
//     restored transactions have already been delivered (see the plugin's
//     restorePurchases: it resolves `error || appStoreError(refreshFailure)`),
//     so a failure there does not mean the restore failed.
const RESTORE_SILENT_CODES = new Set([...CANCEL_CODES, 6777011]);

let initPromise = null;
let handlersRegistered = false;
let onSubscriptionUpdateCb = null;
let userInitiatedAt = 0;
// The SKU the user just ordered with store.order(). Only an `approved` for it
// earns the optimistic Premium patch; restores clear it.
let orderedProductId = null;
// The restore restoreAndWait() is waiting on (see openRestoreSession), or null.
let restoreSession = null;
// Launch re-deliveries already sent to apple-check this session.
const checkedThisLaunch = new Set();

// Transactions the user just approved (purchase or restore), keyed
// transactionId → productId. The `verified` handler drains it. The object
// CdvPurchase hands to `verified` is a VerifiedReceipt, which has NO
// `.transaction` and NO `.productId` — reading them yields undefined, so the
// apple-verify call silently never fired and every Apple subscriber stayed
// invisible to the backend until 2026-09-05. `approved` is the only place the
// transaction id is reliably known, so it queues the id here.
const pendingVerify = new Map();

function getCdv() {
  return typeof window !== 'undefined' ? window.CdvPurchase : null;
}

function getStore() {
  return getCdv()?.store || null;
}

function markUserInitiated() {
  userInitiatedAt = Date.now();
}

function isWithinUserInitiatedWindow() {
  return Date.now() - userInitiatedAt < USER_INITIATED_WINDOW_MS;
}

function isCancel(result) {
  return CANCEL_CODES.has(result?.code) || /cancel/i.test(result?.message || '');
}

function notify(update) {
  if (!onSubscriptionUpdateCb) return;
  try {
    onSubscriptionUpdateCb(update);
  } catch (e) {
    console.warn('[IAP] onSubscriptionUpdate error:', e);
  }
}

async function waitForCdv(timeoutMs = CDV_WAIT_TIMEOUT_MS) {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    const cdv = getCdv();
    if (cdv?.store && cdv?.ProductType && cdv?.Platform) return cdv;
    await new Promise((r) => setTimeout(r, CDV_POLL_INTERVAL_MS));
  }
  return null;
}

/**
 * The outcome of one batch of apple-verify answers (silentFetchDetailed's
 * { ok, status, data }, one per queued transaction):
 *   any success !== false        → 'linked'   (a row was written, or a comp kept)
 *   every answer expired/revoked → 'expired'  (nothing live to link)
 *   a 409                        → 'conflict' (linked to a different account)
 *   anything else                → 'failed'   (5xx, timeout, offline, 400…)
 */
export function classifyVerifyResults(results = []) {
  const answers = results.filter(Boolean);
  if (answers.some((r) => r.ok && r.data && r.data.success !== false)) return 'linked';
  const ended = (r) => r.ok && r.data && (r.data.status === 'expired' || r.data.status === 'revoked');
  if (answers.length && answers.every(ended)) return 'expired';
  if (answers.some((r) => r.status === 409)) return 'conflict';
  return 'failed';
}

function recordOutcome(outcome, productIds) {
  const iap = useIapStore.getState();
  if (outcome === 'linked' || outcome === 'expired') {
    // Linked: the row exists. Ended: nothing live is left to link, so neither
    // the marker nor a "couldn't link" line may survive — they would invite a
    // Restore that can never work.
    iap.setOwnedReceipt(null);
    iap.setLinkError(null);
  } else {
    // 'conflict' | 'failed'. The marker (if the server confirmed one) stays and
    // ages out on its own expiry. A new one is never created here — only from
    // an apple-check answer (recheckUnconfirmed, for a failed batch).
    iap.setLinkError(outcome);
  }
  iap.setLastOutcome(outcome, productIds);
}

// Is this transaction a live subscription that no account has claimed? Asks
// the server (POST /subscription/apple-check, read-only) and sets the
// owned-receipt marker only for state 'live' + linked 'none', with the
// server's expiry. 'live' + 'self' means the link landed after all, so the
// plan is refetched. With no definitive answer the question is kept for the
// next launch or sign-in (iapStore pending checks); a definitive one drops it.
// Never rejects.
async function confirmOwned(productId, transactionId) {
  checkedThisLaunch.add(transactionId);
  const userSub = currentUserSub();
  if (!userSub) return; // signed out: nobody to show it to
  const answer = await silentFetch('/subscription/apple-check', 'POST', { transactionId });
  const state = answer && answer.state;
  if (!DEFINITIVE_CHECK_STATES.has(state)) {
    // Offline: the server was never asked, so the try is not used up.
    notePendingCheck({ transactionId, productId, userSub }, { countAttempt: !isOffline() });
    return;
  }
  forgetPendingCheck(transactionId, userSub);
  if (currentUserSub() !== userSub) return; // the account changed while we asked
  if (state === 'live' && answer.linked === 'none' && answer.expiresAt) {
    useIapStore.getState().setOwnedReceipt({ productId, transactionId, expiresAt: answer.expiresAt });
  } else if (state === 'live' && answer.linked === 'self') {
    notify({ refetch: true });
  }
}

// Launch re-delivery of a known SKU: tell the user "a subscription on this
// device isn't linked" only when the server says it is live and unclaimed.
// Asked once per launch for each transaction.
async function checkOwnedReceipt(productId, transactionId) {
  if (checkedThisLaunch.has(transactionId)) return;
  await confirmOwned(productId, transactionId);
}

// Apple transaction ids are decimal strings that grow over time.
function newerTransactionFirst(a, b) {
  const x = String(a.transactionId);
  const y = String(b.transactionId);
  if (x.length !== y.length) return y.length - x.length;
  if (x === y) return 0;
  return x < y ? 1 : -1;
}

// A user-initiated batch (purchase or restore) whose apple-verify failed:
// StoreKit has already finished these transactions and will never hand them
// back at launch, and the webhook cannot link what has no row. Ask
// apple-check about each one apple-verify did not answer definitively —
// bypassing the once-per-launch rule for launch re-deliveries — so a live,
// unclaimed purchase still leaves the Restore prompt behind (1.4.3 did, with
// an unconfirmed marker). Answers that ended (expired, revoked) need no check.
function recheckUnconfirmed(queued, results) {
  const ended = (r) => Boolean(r && r.ok && r.data && (r.data.status === 'expired' || r.data.status === 'revoked'));
  queued
    .filter((q, i) => !ended(results[i]))
    .sort(newerTransactionFirst)
    .slice(0, MAX_RECHECKS_PER_BATCH)
    .forEach(({ productId, transactionId }) => {
      confirmOwned(productId, transactionId).catch(() => {});
    });
}

let pendingDrain = null;

/**
 * Asks apple-check again about the signed-in account's transactions that got
 * no definitive answer on an earlier try (see confirmOwned). Runs when the
 * store binds (every launch) and on sign-in; each transaction is asked at most
 * once per launch. Resolves when the answers are in. Never rejects.
 */
export function retryPendingChecks() {
  if (!isIOS()) return Promise.resolve();
  // One drain at a time; a sign-in during one is served once it ends.
  if (pendingDrain) return pendingDrain.then(() => retryPendingChecks());
  const due = readPendingChecks().filter((e) => !checkedThisLaunch.has(e.transactionId));
  if (!due.length) return Promise.resolve();
  pendingDrain = Promise.allSettled(due.map((e) => confirmOwned(e.productId, e.transactionId))).then(() => {
    pendingDrain = null;
  });
  return pendingDrain;
}

// One restore at a time. The `approved` handler reports each known-SKU
// delivery to it; the `verified` handler reports each classified answer. It
// resolves once every delivered transaction has an answer, 'nothing' when
// StoreKit delivers none, and 'failed' at the ceiling (or the backstop).
function openRestoreSession({ nothingAfterMs, ceilingMs, backstopMs }) {
  let resolveSession;
  const session = {
    delivered: new Set(),
    answers: new Map(),
    nothingTimer: null,
    ceilingTimer: null,
    done: false,
    promise: new Promise((resolve) => {
      resolveSession = resolve;
    }),
  };
  const backstopTimer = setTimeout(() => session.finish('failed'), backstopMs);
  session.finish = (outcome) => {
    if (session.done) return;
    session.done = true;
    clearTimeout(session.nothingTimer);
    clearTimeout(session.ceilingTimer);
    clearTimeout(backstopTimer);
    if (restoreSession === session) restoreSession = null;
    resolveSession(outcome);
  };
  session.noteDelivery = (transactionId) => {
    session.delivered.add(transactionId);
    clearTimeout(session.nothingTimer);
    session.nothingTimer = null;
  };
  session.noteAnswers = (entries) => {
    entries.forEach(({ transactionId, result }) => {
      if (session.delivered.has(transactionId)) session.answers.set(transactionId, result);
    });
    const all = [...session.delivered];
    if (all.length && all.every((id) => session.answers.has(id))) {
      session.finish(classifyVerifyResults([...session.answers.values()]));
    }
  };
  // StoreKit has handed control back: from here on, the wait is ours.
  session.armTimers = () => {
    if (session.done) return;
    session.ceilingTimer = setTimeout(() => session.finish('failed'), ceilingMs);
    if (session.delivered.size) return;
    session.nothingTimer = setTimeout(() => {
      if (session.delivered.size) return;
      const recent = useIapStore.getState().lastOutcome;
      const repeat = recent && Date.now() - recent.at < RECENT_OUTCOME_MS ? recent.outcome : null;
      session.finish(repeat || 'nothing');
    }, nothingAfterMs);
  };
  return session;
}

export function setOnSubscriptionUpdate(cb) {
  onSubscriptionUpdateCb = typeof cb === 'function' ? cb : null;
}

export function initializeIAP({ onSubscriptionUpdate } = {}) {
  if (!isIOS()) return Promise.resolve();
  if (onSubscriptionUpdate) setOnSubscriptionUpdate(onSubscriptionUpdate);
  if (initPromise) return initPromise;

  initPromise = (async () => {
    const cdv = await waitForCdv();
    if (!cdv) {
      console.warn('[IAP] CdvPurchase plugin not available within timeout');
      initPromise = null;
      return;
    }
    const { store, ProductType, Platform, LogLevel } = cdv;

    if (handlersRegistered) return;
    handlersRegistered = true;

    store.verbosity = process.env.NODE_ENV === 'production' ? LogLevel.WARNING : LogLevel.DEBUG;

    // appAccountToken (canon in-app-purchases.md (c)): StoreKit carries the
    // applicationUsername through to the transaction, and a Cognito sub is
    // already a UUID, so the backend reads the buyer straight back. Evaluated
    // at store.order() time; undefined while signed out. `obfuscator` is a
    // 13.18+ store option (the default md5s it on StoreKit 2) — on 13.13.1 the
    // StoreKit 1 bridge already passes the value through untouched.
    store.applicationUsername = () => currentUserSub() || undefined;
    store.obfuscator = 'disabled';

    store.register(
      REGISTERED_PRODUCT_IDS.map((id) => ({
        id,
        type: ProductType.PAID_SUBSCRIPTION,
        platform: Platform.APPLE_APPSTORE,
      })),
    );

    if (typeof store.error === 'function') {
      store.error((err) => {
        console.warn('[IAP] Store error:', err?.code, err?.message);
      });
    }

    // CdvPurchase v13 builder note: each handler must register via its OWN
    // store.when() call. Chaining .approved().verified() throws at init.
    store.when().approved((transaction) => {
      try {
        if (!transaction.transactionId) {
          transaction.finish();
          return;
        }
        const productId = transaction.products?.[0]?.id;
        const mapping = APPLE_TO_INTERNAL[productId];
        // Best-effort only: CdvPurchase's Transaction exposes `expirationDate`
        // (a Date), but the Apple adapter on 13.13.1 never sets it — expiry is
        // decided server-side (apple-check here, apple-verify below).
        const expiresAt = transaction.expirationDate ? new Date(transaction.expirationDate) : null;
        const isExpired = Boolean(expiresAt && expiresAt.getTime() <= Date.now());
        if (!isWithinUserInitiatedWindow()) {
          // Re-delivered at launch: the device's Apple ID owns this receipt.
          // Never verify outside the window (cross-user contamination guard).
          // Finish it, then ask the server whether it is a live subscription
          // no account has claimed — the only case worth a restore notice.
          transaction.finish();
          if (mapping && !isExpired) {
            checkOwnedReceipt(productId, transaction.transactionId).catch(() => {});
          }
          return;
        }
        // Unknown product = the plugin's virtual `appstore.application`
        // transaction (always present in the Apple receipt, fires approved
        // like a sale) or an unregistered SKU — never queue it for the backend.
        if (!mapping || isExpired) {
          transaction.finish();
          return;
        }
        if (restoreSession) restoreSession.noteDelivery(transaction.transactionId);
        // Only the purchase the user has just made flips the UI before the
        // backend answers. A restore re-delivers every historical
        // transaction, expired ones included — patching for those flashed
        // Premium at a lapsed subscriber and then snapped back to Free.
        if (orderedProductId && productId === orderedProductId) {
          orderedProductId = null;
          notify({
            plan: mapping.plan,
            interval: mapping.interval,
            provider: 'apple',
            productId,
            status: 'active',
            currentPeriodEnd: expiresAt ? expiresAt.toISOString() : null,
          });
        }
        pendingVerify.set(transaction.transactionId, productId);
        transaction.verify();
      } catch (e) {
        console.warn('[IAP] approved handler error:', e);
      }
    });

    store.when().verified((receipt) => {
      try {
        // `receipt` is a VerifiedReceipt: the local transactions live on
        // receipt.sourceReceipt.transactions. Send exactly what `approved`
        // queued (already window-gated), and only ids this receipt carries.
        const localIds = new Set(
          (receipt?.sourceReceipt?.transactions || []).map((t) => t.transactionId).filter(Boolean),
        );
        const queued = [];
        for (const [transactionId, productId] of pendingVerify) {
          if (localIds.size && !localIds.has(transactionId)) continue;
          pendingVerify.delete(transactionId);
          queued.push({ transactionId, productId });
        }
        // One POST per transaction. silentFetchDetailed keeps the status code:
        // 409 (another account) must read differently from a 5xx or timeout.
        const writes = queued.map(({ transactionId, productId }) =>
          silentFetchDetailed('/subscription/apple-verify', 'POST', { transactionId, productId }),
        );
        receipt.finish();
        if (!writes.length) return;
        // Refetch AFTER the backend row exists — invalidating before the
        // write lands races it and snaps the UI back to Free.
        Promise.allSettled(writes).then((settled) => {
          const results = settled.map(
            (s) => (s.status === 'fulfilled' && s.value) || { ok: false, status: 0, data: null },
          );
          const outcome = classifyVerifyResults(results);
          recordOutcome(outcome, [...new Set(queued.map((q) => q.productId))]);
          notify({ refetch: true, outcome });
          if (restoreSession) {
            restoreSession.noteAnswers(
              queued.map((q, i) => ({ transactionId: q.transactionId, result: results[i] })),
            );
          }
          // Not 'conflict': that subscription belongs to another account, and
          // a prompt to restore it here could never work.
          if (outcome === 'failed') recheckUnconfirmed(queued, results);
        });
      } catch (e) {
        console.warn('[IAP] verified handler error:', e);
      }
    });

    // NB: CdvPurchase v13's `When` builder has no `.expired()` method
    // (see node_modules/cordova-plugin-purchase/src/ts/store.ts:413).
    // Expiry detection lives in the App Store Server S2S webhook, which
    // updates DynamoDB; frontend reconciles via /subscription/status.

    try {
      await store.initialize([Platform.APPLE_APPSTORE]);
      try { await store.update(); } catch (e) { /* page poll will retry */ }
      console.log('[IAP] Store initialized, products registered:', store.products?.length || 0);
    } catch (e) {
      console.warn('[IAP] store.initialize failed:', e);
    }

    // Questions an earlier launch could not get answered (see confirmOwned).
    // Not awaited: it needs the network, never StoreKit, and must not hold up
    // a purchase waiting on this bind.
    retryPendingChecks().catch(() => {});
  })();

  return initPromise;
}

// The offer a plain purchase buys: the adapter's Default offer (it carries the
// introductory phase while Apple offers one), else whatever getOffer() gives.
function orderableOffer(product) {
  return (product?.offers || []).find((o) => o?.offerType === 'Default') || product?.getOffer?.() || null;
}

/**
 * Sellable products as StoreKit currently prices them:
 * [{ id, title, price, priceMicros, currency, billingPeriod, trialDays }].
 * `price` is the BILLED (recurring) amount — never the £0.00 trial phase that
 * `product.pricing` returns on a trial-eligible account. `trialDays` is the
 * introductory free trial while the adapter offers one, else null. Products
 * StoreKit has not delivered yet are left out.
 */
export function getProducts() {
  const store = getStore();
  if (!store) return [];

  return PRODUCT_IDS.map((id) => {
    let product = null;
    try {
      product = store.get(id);
    } catch (e) {
      product = null;
    }
    if (!product) return null;
    const billed = getBilledPricing(product);
    if (!billed) return null;
    return {
      id: product.id,
      title: product.title,
      price: billed.price,
      priceMicros: billed.priceMicros,
      currency: billed.currency,
      billingPeriod: billed.billingPeriod,
      trialDays: getIntroTrial(product)?.periodDays ?? null,
    };
  }).filter(Boolean);
}

export async function refreshProducts() {
  await initializeIAP();
  const store = getStore();
  if (!store) return;
  try { await store.update(); } catch (e) { /* ignore */ }
}

// Active poll for a specific product. Pulse `store.update()` every 3s — iPadOS
// sandbox sometimes needs repeated pokes to deliver the catalogue.
async function whenProductLoaded(productId, timeoutMs = PRODUCT_POLL_TIMEOUT_MS) {
  const store = getStore();
  if (!store) return null;
  const start = Date.now();

  const initial = store.get(productId);
  const initialOffer = orderableOffer(initial);
  if (initial && initialOffer) return { product: initial, offer: initialOffer };

  try { store.update(); } catch (e) { /* ignore */ }
  let nextPulseAt = PRODUCT_PULSE_INTERVAL_MS;

  while (Date.now() - start < timeoutMs) {
    const product = store.get(productId);
    const offer = orderableOffer(product);
    if (product && offer) return { product, offer };
    const elapsed = Date.now() - start;
    if (elapsed > nextPulseAt && nextPulseAt < timeoutMs - 1000) {
      try { store.update(); } catch (e) { /* ignore */ }
      nextPulseAt += PRODUCT_PULSE_INTERVAL_MS;
    }
    await new Promise((r) => setTimeout(r, PRODUCT_POLL_INTERVAL_MS));
  }
  return null;
}

export async function whenAnyProductLoaded(timeoutMs = PRODUCT_POLL_TIMEOUT_MS) {
  await initializeIAP();
  const store = getStore();
  if (!store) return false;
  const start = Date.now();

  for (const id of PRODUCT_IDS) {
    const p = store.get(id);
    if (p && orderableOffer(p)) return true;
  }

  try { store.update(); } catch (e) { /* ignore */ }
  let nextPulseAt = PRODUCT_PULSE_INTERVAL_MS;

  while (Date.now() - start < timeoutMs) {
    for (const id of PRODUCT_IDS) {
      const p = store.get(id);
      if (p && orderableOffer(p)) return true;
    }
    const elapsed = Date.now() - start;
    if (elapsed > nextPulseAt && nextPulseAt < timeoutMs - 1000) {
      try { store.update(); } catch (e) { /* ignore */ }
      nextPulseAt += PRODUCT_PULSE_INTERVAL_MS;
    }
    await new Promise((r) => setTimeout(r, PRODUCT_POLL_INTERVAL_MS));
  }
  return false;
}

export async function isReady(productId) {
  const store = getStore();
  if (!store) return false;
  const product = store.get(productId);
  return Boolean(product && orderableOffer(product));
}

export async function purchaseProduct(productId) {
  await initializeIAP();
  const store = getStore();
  if (!store) {
    const err = new Error('PRODUCT_TIMEOUT');
    err.code = 'PRODUCT_TIMEOUT';
    throw err;
  }

  const ready = await whenProductLoaded(productId);
  if (!ready) {
    const err = new Error('PRODUCT_TIMEOUT');
    err.code = 'PRODUCT_TIMEOUT';
    throw err;
  }

  markUserInitiated();
  orderedProductId = productId;
  let result;
  try {
    result = await store.order(ready.offer);
  } catch (e) {
    orderedProductId = null;
    throw e;
  }
  if (result && result.isError) {
    orderedProductId = null;
    if (isCancel(result)) {
      const cancelled = new Error('canceled');
      cancelled.cancelled = true;
      cancelled.code = result.code;
      throw cancelled;
    }
    const err = new Error(result.message || 'Purchase failed. Please try again.');
    err.code = result.code;
    throw err;
  }
  return result;
}

/**
 * Restore Purchases, reporting what it actually found:
 *   'linked'    a subscription was restored to this account
 *   'expired'   everything StoreKit returned has ended (or was refunded)
 *   'conflict'  it belongs to a different BabyTrack account (409)
 *   'failed'    the restore or the backend failed, or 25 s passed after
 *               StoreKit handed back control
 *   'nothing'   StoreKit delivered no BabyTrack subscription
 *   'cancelled' the user dismissed the App Store sign-in — show nothing
 * Never rejects. A second call while one is running shares its result.
 * `options` exist for tests; callers use the defaults.
 */
export async function restoreAndWait({
  nothingAfterMs = RESTORE_NOTHING_AFTER_MS,
  ceilingMs = RESTORE_CEILING_MS,
  backstopMs = RESTORE_BACKSTOP_MS,
} = {}) {
  if (restoreSession) return restoreSession.promise;
  await initializeIAP();
  const store = getStore();
  if (!store) return 'failed';
  if (restoreSession) return restoreSession.promise;

  const session = openRestoreSession({ nothingAfterMs, ceilingMs, backstopMs });
  restoreSession = session;
  markUserInitiated();
  orderedProductId = null; // a restore re-delivery never patches the plan
  useIapStore.getState().setLinkError(null);

  // Not awaited: the session's own timers decide when to stop waiting, so a
  // StoreKit that never answers still ends at the backstop.
  Promise.resolve()
    .then(() => store.restorePurchases())
    .then((result) => {
      // Same contract as store.order(): a StoreKit restore failure RESOLVES
      // with an IError rather than throwing. A cancel and a post-restore
      // receipt refresh (6777011) are never failures — see RESTORE_SILENT_CODES.
      if (result && result.isError && isCancel(result)) {
        session.finish('cancelled');
      } else if (result && result.isError && !RESTORE_SILENT_CODES.has(result.code)) {
        session.finish('failed');
      } else {
        session.armTimers();
      }
    })
    .catch(() => session.finish('failed'));
  return session.promise;
}

export function getAppleProductId(plan, interval) {
  return PRODUCT_ID_MAP[`${plan}_${interval}`] || null;
}
